/**
 * SignInCard — RIGHT-side sign-in card on the login page.
 *
 * Owns password/OTP tab state, the staff 2FA challenge sub-flow and their
 * API calls. Renders the maintenance-mode fallback when customer sign-in
 * is disabled from CMS.
 */
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import {
  LogIn, ShieldCheck, Zap, KeyRound, ArrowRight,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/api/client";

const ROLE_ROUTES = {
  admin: "/app/dashboard", sales: "/app/dashboard", dispatcher: "/app/dashboard",
  accounts: "/app/dashboard", operator: "/app/dashboard",
  driver: "/driver", customer: "/portal/dashboard",
};

export function SignInCard({ isStaff, brand, customerTheme, staffTheme, onOpenForgot, email, setEmail }) {
  const nav = useNavigate();
  const { login, verify2fa } = useAuth();
  const customerLoginDisabled = !isStaff && brand.customer_login_enabled === false;

  const [tab, setTab] = useState("password");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const [phone, setPhone] = useState("+91 9000000101");
  const [otpSent, setOtpSent] = useState(false);
  const [otpMock, setOtpMock] = useState("");
  const [otp, setOtp] = useState("");
  const [otpLoading, setOtpLoading] = useState(false);

  const [twoFa, setTwoFa] = useState(null);
  const [twoFaOtp, setTwoFaOtp] = useState("");
  const [twoFaLoading, setTwoFaLoading] = useState(false);

  const submitPassword = async (e) => {
    e.preventDefault();
    if (customerLoginDisabled) {
      toast.error("Customer sign-in is temporarily unavailable.");
      return;
    }
    setLoading(true);
    try {
      const result = await login(email, password);
      if (result?.requires_2fa) {
        if (!isStaff) {
          toast.error("This account can't sign in here.");
          setLoading(false);
          return;
        }
        setTwoFa({
          challenge_id: result.challenge_id,
          masked_email: result.masked_email,
          masked_phone: result.masked_phone,
          ttl_seconds: result.ttl_seconds,
        });
        toast.success("OTP sent — check your phone / notifications");
        setLoading(false);
        return;
      }
      const u = result;
      if (isStaff && u.role === "customer") {
        toast.error("Customers should sign in on the customer login page.");
        setLoading(false);
        return;
      }
      if (!isStaff && u.role !== "customer") {
        toast.error("This account signs in on the staff/driver login page.");
        setLoading(false);
        return;
      }
      toast.success(`Welcome ${u.name}`);
      nav(ROLE_ROUTES[u.role] || "/app/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const submitTwoFa = async (e) => {
    e?.preventDefault();
    if (!twoFa || twoFaOtp.length !== 4) return;
    setTwoFaLoading(true);
    try {
      const u = await verify2fa(twoFa.challenge_id, twoFaOtp);
      toast.success(`Welcome ${u.name}`);
      nav(ROLE_ROUTES[u.role] || "/app/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "OTP verification failed");
    } finally {
      setTwoFaLoading(false);
    }
  };

  const requestOtp = async () => {
    setOtpLoading(true);
    try {
      const { data } = await api.post("/auth/otp/request", { phone });
      setOtpSent(true);
      setOtpMock(data.otp_mock);
      toast.success(`OTP sent (mock): ${data.otp_mock}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send OTP");
    } finally {
      setOtpLoading(false);
    }
  };

  const verifyOtp = async () => {
    setOtpLoading(true);
    try {
      const { data } = await api.post("/auth/otp/verify", { phone, otp });
      sessionStorage.setItem("ddd_user", JSON.stringify(data.user));
      toast.success(`Welcome ${data.user.name}`);
      window.location.href = ROLE_ROUTES[data.user.role] || "/app/dashboard";
    } catch (err) {
      toast.error(err.response?.data?.detail || "OTP verification failed");
    } finally {
      setOtpLoading(false);
    }
  };

  const cardStyle = isStaff
    ? {
        backgroundColor: `${staffTheme.card_bg}CC`,
        color: staffTheme.text,
        borderColor: "rgba(255,255,255,0.10)",
        backdropFilter: "blur(12px)",
        boxShadow: `0 25px 50px -12px ${staffTheme.accent}20`,
      }
    : {
        backgroundColor: customerTheme.card_bg,
        boxShadow: `0 25px 50px -12px ${customerTheme.accent}20`,
        borderColor: `${customerTheme.accent}22`,
      };
  const inputStyle = isStaff
    ? { backgroundColor: "#1E293B", color: staffTheme.text, borderColor: "rgba(255,255,255,0.1)" }
    : undefined;
  const btnStyle = isStaff
    ? { background: `linear-gradient(90deg, ${staffTheme.accent}, ${staffTheme.accent_secondary})` }
    : { backgroundColor: customerTheme.accent };
  const mutedStyle = isStaff ? { color: staffTheme.muted_text } : undefined;
  const badgeStyle = isStaff
    ? { backgroundColor: `${staffTheme.accent}26`, color: staffTheme.accent, boxShadow: `inset 0 0 0 1px ${staffTheme.accent}55` }
    : { backgroundColor: `${customerTheme.accent}1F`, color: customerTheme.accent };

  return (
    <Card className="w-full max-w-md shadow-2xl" style={cardStyle} data-testid="login-card">
      <CardHeader className="space-y-2 pb-2">
        <div className="flex items-center justify-between">
          <div className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-widest" style={badgeStyle}>
            {isStaff ? <><KeyRound size={11} /> Staff</> : <><Zap size={11} /> Customer</>}
          </div>
          <div className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: isStaff ? staffTheme.muted_text : "#94A3B8" }}>
            Secure · TLS 1.3
          </div>
        </div>
        <CardTitle className="font-display text-3xl">
          {isStaff ? "Staff & driver sign-in" : "Welcome back."}
        </CardTitle>
        <CardDescription style={{ color: isStaff ? staffTheme.muted_text : "#64748B" }}>
          {isStaff
            ? "For administrators, dispatchers, drivers, accounts and operators only."
            : "Sign in to place orders, top up your wallet, and track deliveries in real-time."}
        </CardDescription>
      </CardHeader>

      <CardContent>
        {customerLoginDisabled ? (
          <MaintenanceNotice customerTheme={customerTheme} />
        ) : (
          <Tabs value={tab} onValueChange={setTab} className="w-full">
            <TabsList
              className={`grid w-full ${isStaff ? "grid-cols-1" : "grid-cols-2"}`}
              style={isStaff ? { backgroundColor: "#1E293B" } : undefined}
            >
              <TabsTrigger value="password" data-testid="tab-password">Email · Password</TabsTrigger>
              {!isStaff && <TabsTrigger value="otp" data-testid="tab-otp">Phone · OTP</TabsTrigger>}
            </TabsList>

            <TabsContent value="password">
              <form onSubmit={twoFa ? submitTwoFa : submitPassword} className="mt-4 space-y-4" data-testid={twoFa ? "staff-2fa-form" : undefined}>
                <div className="space-y-2">
                  <Label htmlFor="email" style={mutedStyle}>Email</Label>
                  <Input
                    id="email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={!!twoFa}
                    style={inputStyle}
                    data-testid="input-email"
                  />
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="password" style={mutedStyle}>Password</Label>
                    {!twoFa && (
                      <button
                        type="button"
                        onClick={onOpenForgot}
                        className="text-xs font-semibold hover:underline"
                        style={{ color: (isStaff ? staffTheme.accent : customerTheme.accent) }}
                        data-testid="login-forgot-password-link"
                      >
                        Forgot?
                      </button>
                    )}
                  </div>
                  <Input
                    id="password"
                    type="password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={!!twoFa}
                    style={inputStyle}
                    data-testid="input-password"
                  />
                </div>

                {twoFa && (
                  <>
                    <div
                      className="rounded-xl border p-3 text-xs"
                      style={isStaff
                        ? { borderColor: `${staffTheme.accent}55`, background: `${staffTheme.accent}12`, color: staffTheme.text }
                        : { borderColor: `${customerTheme.accent}55`, background: `${customerTheme.accent}0F` }}
                    >
                      <div className="mb-1 flex items-center gap-2 font-semibold">
                        <ShieldCheck size={14} style={{ color: isStaff ? staffTheme.accent : customerTheme.accent }} />
                        Enter the 4-digit code sent to {twoFa.masked_phone || twoFa.masked_email}
                      </div>
                      <div className="opacity-70">Expires in {Math.round((twoFa.ttl_seconds || 300) / 60)} min.</div>
                    </div>
                    <div className="space-y-2">
                      <Label style={mutedStyle}>SMS OTP</Label>
                      <div className="flex justify-center">
                        <InputOTP maxLength={4} value={twoFaOtp} onChange={setTwoFaOtp} data-testid="staff-2fa-otp" autoFocus>
                          <InputOTPGroup>
                            {[0, 1, 2, 3].map((i) => <InputOTPSlot key={i} index={i} />)}
                          </InputOTPGroup>
                        </InputOTP>
                      </div>
                    </div>
                  </>
                )}

                {twoFa ? (
                  <div className="flex gap-2">
                    <Button type="button" variant="outline" onClick={() => { setTwoFa(null); setTwoFaOtp(""); }} className="flex-1" data-testid="btn-2fa-back">
                      Back
                    </Button>
                    <Button type="submit" className="flex-[2] font-semibold text-white transition-opacity hover:opacity-90" style={btnStyle} disabled={twoFaLoading || twoFaOtp.length !== 4} data-testid="btn-2fa-verify">
                      <ShieldCheck size={14} className="mr-2" />
                      {twoFaLoading ? "Verifying..." : "Verify & sign in"}
                    </Button>
                  </div>
                ) : (
                  <Button type="submit" className="w-full font-semibold text-white transition-opacity hover:opacity-90" style={btnStyle} disabled={loading} data-testid="btn-login">
                    <LogIn size={16} className="mr-2" />
                    {loading ? "Signing in..." : `Sign in ${isStaff ? "to console" : "→"}`}
                  </Button>
                )}
              </form>
            </TabsContent>

            {!isStaff && (
              <TabsContent value="otp">
                <div className="mt-4 space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone</Label>
                    <Input id="phone" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+91 90000 00000" data-testid="input-phone" />
                  </div>
                  {!otpSent ? (
                    <Button className="w-full text-white" style={{ backgroundColor: customerTheme.accent }} onClick={requestOtp} disabled={otpLoading} data-testid="btn-send-otp">
                      {otpLoading ? "Sending..." : "Send OTP"}
                    </Button>
                  ) : (
                    <>
                      <div className="space-y-2">
                        <Label>Enter OTP {otpMock && <span className="text-xs text-muted-foreground">(mock: {otpMock})</span>}</Label>
                        <InputOTP maxLength={4} value={otp} onChange={setOtp} data-testid="input-otp">
                          <InputOTPGroup>
                            {[0, 1, 2, 3].map((i) => <InputOTPSlot key={i} index={i} />)}
                          </InputOTPGroup>
                        </InputOTP>
                      </div>
                      <Button className="w-full text-white" style={{ backgroundColor: customerTheme.accent }} onClick={verifyOtp} disabled={otpLoading || otp.length !== 4} data-testid="btn-verify-otp">
                        {otpLoading ? "Verifying..." : "Verify & sign in"}
                      </Button>
                    </>
                  )}
                </div>
              </TabsContent>
            )}
          </Tabs>
        )}

        {!customerLoginDisabled && (
          <>
            <div className="mt-5 flex items-center justify-center gap-1 text-sm" style={{ color: isStaff ? staffTheme.muted_text : "#64748B" }}>
              {isStaff ? (
                <span className="opacity-60">Authorized personnel only.</span>
              ) : (
                <>
                  <span>New here?</span>
                  <Link to="/register" className="font-semibold hover:underline" style={{ color: customerTheme.accent }} data-testid="link-register">
                    Create an account
                  </Link>
                </>
              )}
            </div>
            <div className="mt-6 flex items-center justify-center gap-1.5 text-[10px] font-semibold uppercase tracking-widest" style={{ color: isStaff ? staffTheme.muted_text : "#94A3B8" }}>
              <ShieldCheck size={11} />
              Protected by JWT · HTTPS · Rate limited
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function MaintenanceNotice({ customerTheme }) {
  return (
    <div
      className="mt-2 space-y-4 rounded-xl border p-6 text-center"
      style={{ borderColor: `${customerTheme.accent}55`, background: `${customerTheme.accent}0F` }}
      data-testid="customer-login-disabled"
    >
      <div
        className="mx-auto grid h-12 w-12 place-items-center rounded-full"
        style={{ background: `${customerTheme.accent}22`, color: customerTheme.accent }}
      >
        <ShieldCheck size={22} />
      </div>
      <div>
        <div className="font-display text-lg font-bold" style={{ color: "#0F172A" }}>
          Customer sign-in is temporarily unavailable
        </div>
        <p className="mt-1 text-sm text-slate-600">
          We&apos;re performing scheduled maintenance. Please check back shortly, or reach out to our support team for urgent assistance.
        </p>
      </div>
      <div className="flex flex-col items-center gap-2 pt-2">
        <Link
          to="/#contact"
          className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-xs font-semibold text-white"
          style={{ background: customerTheme.accent }}
          data-testid="disabled-contact-support"
        >
          Contact support <ArrowRight size={12} />
        </Link>
        <Link
          to="/"
          className="text-xs font-semibold hover:underline"
          style={{ color: customerTheme.accent }}
          data-testid="disabled-back-home"
        >
          Back to home
        </Link>
      </div>
    </div>
  );
}
